/**
 * LoginPage
 * Admin/staff login entrypoint (email/password) for dashboard access.
 *
 * Post-login navigation:
 * - Redirects to `location.state.from` if set by ProtectedRoute
 * - Falls back to `/dashboard`
 */
import { startTransition, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isFirebaseConfigured } from '../lib/firebase/config';
import { isDashboardRole } from '../services/userService';

export default function LoginPage() {
  const { user, profile, signInPassword, signOut, authError } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fromPath = location.state?.from || '/dashboard';

  useEffect(() => {
    if (user && profile && isDashboardRole(profile.role)) {
      startTransition(() => {
        navigate(fromPath, { replace: true });
      });
    }
  }, [fromPath, navigate, profile, user]);

  useEffect(() => {
    if (user && profile && !isDashboardRole(profile.role)) {
      setErrorMessage(
        'This account is authenticated but not authorized for dashboard access. Set users/{uid}.role to admin or editor.'
      );
    }
  }, [profile, user]);

  async function handleEmailSubmit(event) {
    event.preventDefault();
    setErrorMessage('');
    setPending(true);

    try {
      const result = await signInPassword(email, password);
      if (!isDashboardRole(result?.profile?.role)) {
        setErrorMessage(
          'Signed in, but this account is not an admin/editor yet. Set users/{uid}.role to admin or editor.'
        );
      }
    } catch (error) {
      setErrorMessage(error.message ?? 'Authentication failed.');
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="login-page">
      <div className="login-card">
        <p className="eyebrow">Protected Access</p>
        <h1>Sign in</h1>
        <p>
          Authorized staff can access the dashboard to manage articles, media,
          and staff user roles.
        </p>

        {!isFirebaseConfigured ? (
          <p className="form-error">
            Firebase is not configured yet. Add the `.env` values and restart
            the app.
          </p>
        ) : null}

        {errorMessage ? <p className="form-error">{errorMessage}</p> : null}
        {authError ? <p className="form-error">{authError}</p> : null}
        {user && profile && !isDashboardRole(profile.role) ? (
          <button type="button" onClick={signOut}>
            Sign out and switch account
          </button>
        ) : null}

        <form className="editor-form" onSubmit={handleEmailSubmit}>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>

          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={6}
              required
            />
          </label>

          <button type="submit" disabled={!isFirebaseConfigured || pending}>
            {pending ? 'Signing in...' : 'Sign in with email'}
          </button>
        </form>
      </div>
    </section>
  );
}
